import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { billingApi, type BillingCycle, type CatalogProduct } from '../api/client'
import { formatMoney, catalogPrice } from '../lib/pricing'
import { useDisplayCurrency } from '../store/currencyStore'
import { useCartStore } from '../store/cartStore'
import CurrencyPicker from '../components/CurrencyPicker'
import StorefrontNav from '../components/StorefrontNav'
import PaymentLogos from '../components/PaymentLogos'
import StorefrontFooter from '../components/StorefrontFooter'

// Product catalogue. Every purchasable service is listed with its price, its
// characteristics and an "add to cart" action — the shape an acquiring bank
// expects to see before enabling a virtual POS, and the entry point of the
// real buying flow.
export default function ProductsPage() {
  const { t, i18n } = useTranslation()
  const currency = useDisplayCurrency()
  const cycle = useCartStore((s) => s.cycle)
  const setCycle = useCartStore((s) => s.setCycle)

  const { data, isLoading } = useQuery({
    queryKey: ['catalog'],
    queryFn: billingApi.getCatalog,
  })

  const products = data?.products.filter((p) => p.purchasable) ?? []

  return (
    <div className="relative min-h-screen bg-[#fbfbfa] flex flex-col">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[420px] bg-[radial-gradient(90%_120%_at_85%_-10%,rgba(2,132,199,0.08),transparent_55%)]" />
      <StorefrontNav />

      <div className="relative flex-1 max-w-5xl w-full mx-auto px-6 py-14">
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight mb-3">
            {t('products.title')}
          </h1>
          <p className="text-gray-500 max-w-2xl">{t('products.subtitle')}</p>
        </div>

        <div className="flex flex-wrap items-center gap-4 mb-8">
          <CurrencyPicker />
          <CycleToggle cycle={cycle} onChange={setCycle} />
        </div>

        {isLoading && <p className="text-sm text-gray-500">{t('common.loading')}</p>}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {products.map((p) => (
            <ProductCard key={p.sku} product={p} cycle={cycle} currency={currency} lang={i18n.language} />
          ))}
        </div>

        <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-4 justify-between rounded-2xl border border-gray-200 bg-white p-6">
          <div>
            <h2 className="text-lg font-bold text-gray-900 mb-1">{t('products.enterpriseTitle')}</h2>
            <p className="text-sm text-gray-500">{t('products.enterpriseText')}</p>
          </div>
          <a
            href="mailto:info@provisyer.com"
            className="shrink-0 px-6 py-3 rounded-xl text-sm font-semibold bg-gray-900 text-white hover:bg-gray-800 transition-colors text-center"
          >
            {t('products.enterpriseCta')}
          </a>
        </div>

        <div className="mt-10">
          <PaymentLogos withCaption />
        </div>
      </div>

      <StorefrontFooter />
    </div>
  )
}

export function CycleToggle({
  cycle,
  onChange,
}: {
  cycle: BillingCycle
  onChange: (c: BillingCycle) => void
}) {
  const { t } = useTranslation()
  return (
    <div className="inline-flex items-center bg-gray-100 rounded-xl p-1">
      {(['monthly', 'yearly'] as BillingCycle[]).map((c) => (
        <button
          key={c}
          onClick={() => onChange(c)}
          className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors ${
            cycle === c ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          {c === 'monthly' ? t('pricing.cycleMonthly') : t('pricing.cycleYearly')}
        </button>
      ))}
    </div>
  )
}

// Per-SKU feature key lists. Kept explicit (rather than an i18next array) so a
// missing translation surfaces as one visible key instead of an empty list.
export const PRODUCT_FEATURE_KEYS: Record<string, string[]> = {
  slot: ['f1', 'f2', 'f3', 'f4', 'f5', 'f6'],
  mail: ['f1', 'f2', 'f3', 'f4', 'f5'],
}

function ProductCard({
  product,
  cycle,
  currency,
  lang,
}: {
  product: CatalogProduct
  cycle: BillingCycle
  currency: 'TRY' | 'USD' | 'EUR'
  lang: string
}) {
  const { t } = useTranslation()
  const add = useCartStore((s) => s.add)
  const [added, setAdded] = useState(false)
  const price = catalogPrice(product, cycle, currency)

  const handleAdd = () => {
    add(product.sku, 1, product.max_quantity)
    setAdded(true)
    window.setTimeout(() => setAdded(false), 1800)
  }

  return (
    <div className="bg-white rounded-2xl border-2 border-gray-200 p-6 flex flex-col">
      <h2 className="text-xl font-bold text-gray-900 mb-1">{t(`products.${product.sku}.name`)}</h2>
      <p className="text-sm text-gray-500 mb-5">{t(`products.${product.sku}.tagline`)}</p>

      <div className="mb-5">
        <div className="flex items-baseline gap-1">
          <span className="text-3xl font-bold text-gray-900">{formatMoney(price, currency, lang)}</span>
          <span className="text-gray-400 text-sm">
            /{cycle === 'yearly' ? t('pricing.yr') : t('pricing.mo')}
          </span>
        </div>
        <p className="text-xs text-gray-400 mt-1">
          {t('products.vatNote', { rate: product.vat_rate })}
        </p>
      </div>

      <ul className="space-y-2 mb-6 flex-1">
        {(PRODUCT_FEATURE_KEYS[product.sku] ?? []).map((k) => (
          <li key={k} className="flex items-start gap-2 text-sm text-gray-600">
            <svg className="w-4 h-4 text-green-500 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            {t(`products.${product.sku}.${k}`)}
          </li>
        ))}
      </ul>

      {product.needs_instance && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4">
          {t('products.needsInstanceNote')}
        </p>
      )}

      <div className="flex items-center gap-3">
        <button
          onClick={handleAdd}
          className="flex-1 py-3 rounded-xl text-sm font-semibold bg-brand-600 text-white hover:bg-brand-700 transition-colors"
        >
          {added ? t('products.added') : t('products.addToCart')}
        </button>
        <Link
          to={`/urunler/${product.slug}`}
          className="px-4 py-3 rounded-xl text-sm font-semibold border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors"
        >
          {t('products.details')}
        </Link>
      </div>
    </div>
  )
}
